import { useAuth0 } from '@auth0/auth0-react';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import { treatmentPlanV3Actions } from '../../../../../../store';
import useTreatmentPlanV3MedicationPageState from './useTreatmentPlanV3MedicationPageState';

import type { AppDispatch } from '../../../../../../store';
import type { MedicationPayload } from '../../../../../common';
import type {
  TreatmentPlanV3MedicationsDependencies,
  UseTreatmentPlanV3MedicationsResult,
} from './types';

export default function useTreatmentPlanV3Medications() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const pageState = useTreatmentPlanV3MedicationPageState();

  return useTreatmentPlanV3MedicationsWithDependencies({
    actions: treatmentPlanV3Actions,
    dispatch,
    getAccessTokenSilently,
    treatmentPlanId,
    ...pageState,
  });
}

export function useTreatmentPlanV3MedicationsWithDependencies({
  actions,
  dispatch,
  getAccessTokenSilently,
  treatmentPlanId,
  medications,
  noMedication,
  isLoading,
  hasLoaded,
  hasLoadError,
  isSaving,
  isBulkSaving,
}: TreatmentPlanV3MedicationsDependencies): UseTreatmentPlanV3MedicationsResult {
  const create = useCallback(
    async (data: MedicationPayload) => {
      if (!treatmentPlanId) return undefined;
      const result = await dispatch(
        actions.createTreatmentPlanV3Medication({ treatmentPlanId, data, getAccessTokenSilently }),
      );
      return actions.createTreatmentPlanV3Medication.fulfilled.match(result)
        ? result.payload.id
        : undefined;
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const update = useCallback(
    async (medicationId: number, data: MedicationPayload) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.updateTreatmentPlanV3Medication({
          treatmentPlanId,
          medicationId,
          data,
          getAccessTokenSilently,
        }),
      );
      return actions.updateTreatmentPlanV3Medication.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const remove = useCallback(
    async (medicationId: number) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.deleteTreatmentPlanV3Medication({
          treatmentPlanId,
          medicationId,
          getAccessTokenSilently,
        }),
      );
      return actions.deleteTreatmentPlanV3Medication.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  const clearAll = useCallback(async () => {
    if (!treatmentPlanId) return false;
    const result = await dispatch(
      actions.deleteAllTreatmentPlanV3Medications({ treatmentPlanId, getAccessTokenSilently }),
    );
    return actions.deleteAllTreatmentPlanV3Medications.fulfilled.match(result);
  }, [actions, dispatch, getAccessTokenSilently, treatmentPlanId]);

  const setNoMedication = useCallback(
    async (nextNoMedication: boolean) => {
      if (!treatmentPlanId) return false;
      const result = await dispatch(
        actions.setTreatmentPlanV3NoMedication({
          treatmentPlanId,
          noMedication: nextNoMedication,
          getAccessTokenSilently,
        }),
      );
      return actions.setTreatmentPlanV3NoMedication.fulfilled.match(result);
    },
    [actions, dispatch, getAccessTokenSilently, treatmentPlanId],
  );

  return {
    medications,
    noMedication,
    isLoading,
    hasLoaded,
    hasLoadError,
    isSaving,
    isBulkSaving,
    create,
    update,
    remove,
    clearAll,
    setNoMedication,
  };
}
